import React, { useEffect, useRef, useState } from 'react';
import {
  useSchedulerShiftsListQuery,
  SchedulerShiftsListQuery,
} from '../../../__generated__/graphql';
import { BryntumCalendar, BryntumCalendarProps } from '@bryntum/calendar-react';
import {
  Calendar,
  CalendarView,
  EventModel,
  EventModelConfig,
} from '@bryntum/calendar';
import { CalendarViewMode } from './types';
import { getDateRangeForMode } from './utils';
import { mapLocaleToBryntumLocale } from './localeUtils';
import { useUserInfo } from '../../../context/UserInfo';
import ServerError from '../../../../../components/ServerError';
import CalendarLoading from './CalendarLoading';
import './styles.css';
import { EventContent } from './EventContent';
import { ShiftLabel } from './types';
import { useCurrentLocale } from '../../../../../context/I18n';

type EventModalData = EventModel & {
  shiftLabels: ShiftLabel[];
  breakPeriodMinutes?: number;
};

type ShiftsCalendarProps = {
  currentDay: Date;
  mode: CalendarViewMode;
  onDateChange: (date: Date) => void;
  onModeChange: (mode: CalendarViewMode) => void;
  data: SchedulerShiftsListQuery;
  loading: boolean;
};

const ShiftsCalendarView: React.FC<ShiftsCalendarProps> = ({
  currentDay,
  mode,
  onDateChange,
  onModeChange,
  data,
  loading,
}) => {
  const locale = useCurrentLocale();
  const calendarRef = useRef<BryntumCalendar>(null);

  const handleDateChange = (event: {
    source: Calendar;
    oldDate: Date;
    date: Date;
  }) => {
    onDateChange(event.date);
  };

  const handleActiveItemChange = ({
    source,
  }: {
    source: Calendar;
    prevActiveItem: CalendarView;
    activeItem: CalendarView;
    containedFocus: boolean;
  }) => {
    const newMode = source.mode;
    if (newMode && newMode !== mode) {
      onModeChange(newMode);
    }
  };

  useEffect(() => {
    const calendar = calendarRef.current?.instance;
    if (calendar) {
      const bryntumLocaleName = mapLocaleToBryntumLocale(locale);
      calendar.localeManager.locale = bryntumLocaleName;
    }
  }, [locale]);

  useEffect(() => {
    if (!loading && data?.schedulerShifts) {
      const bryntumEvents: EventModelConfig[] = data.schedulerShifts.map(
        (shift) => ({
          id: shift.Id,
          name: shift.flair__Title__c || '',
          startDate: new Date(shift.flair__Start_Datetime__c),
          endDate: new Date(shift.flair__End_Datetime__c),
          resourceId: shift.flair__Linked_Resource_Id__c,
          breakPeriodMinutes:
            shift.flair__Total_Break_Period_in_Minutes__c || 0,
          shiftLabels:
            shift.shiftLabels?.map((label) => ({
              id: label.Id,
              name: label.Name,
              tagName: label.flair__Tag_Name__c,
            })) || [],
        }),
      );

      const calendar = calendarRef.current?.instance;
      if (calendar) {
        calendar.eventStore.data = bryntumEvents;
      }
    }
  }, [data, loading]);

  const customEventRenderer = (detail: { eventRecord: EventModel }) => {
    const { eventRecord } = detail;
    const event = eventRecord as EventModalData;
    return (
      <EventContent
        name={event.name || ''}
        shiftLabels={event?.shiftLabels}
        breakPeriodMinutes={event.breakPeriodMinutes}
      />
    );
  };

  const calendarConfig: BryntumCalendarProps = {
    date: currentDay,
    mode,
    onDateChange: handleDateChange,
    onActiveItemChange: handleActiveItemChange,
    readOnly: true,
    eventEditFeature: false,
    modes: {
      day: {
        showAllDayHeader: false,
        eventRenderer: (data) => customEventRenderer(data),
      },
      week: {
        showAllDayHeader: false,
        eventRenderer: (data) => customEventRenderer(data),
      },
    },
  };

  return (
    <div className="d-flex flex-column calendar-height">
      <BryntumCalendar ref={calendarRef} {...calendarConfig} />
    </div>
  );
};

export const SchedulerShiftsCalendar: React.FC = () => {
  const authUserInfo = useUserInfo();

  const [currentDay, setCurrentDay] = useState(new Date());
  const [mode, setMode] = useState<CalendarViewMode>('week');

  const { startDate, endDate } = getDateRangeForMode(currentDay, mode);

  const { data, loading, error } = useSchedulerShiftsListQuery({
    variables: {
      filter: { startDate, endDate, employeesIds: [authUserInfo.id] },
    },
  });

  if (loading) {
    return <CalendarLoading currentMode={mode} />;
  }

  if (!data || error) {
    return <ServerError />;
  }

  return (
    <ShiftsCalendarView
      currentDay={currentDay}
      mode={mode}
      onDateChange={setCurrentDay}
      onModeChange={setMode}
      data={data}
      loading={loading}
    />
  );
};
